import React, {useEffect, useState, useCallback} from "react";
import "./MqttEvent.css";
import Badge from "react-bootstrap/Badge";
import ReactJsonView from "react-json-view";
import {faAndroid, faApple, faGoogle, faAmazon} from "@fortawesome/free-brands-svg-icons";
import LoadingIcon from "../../../../../Partials/LoadingIcon/LoadingIcon";
import { useTimezone } from "../../../../../../contexts/TimezoneContext";
import {getBondColor} from "../../../../../../../../lib/ui/colorUtils.js";
import {padDeviceId} from "../../../../../../../../lib/utils";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp, faUser, faMobileScreenButton} from "@fortawesome/free-solid-svg-icons";

// client_id of the form "<user uuid>-<session suffix>"
const CLIENT_ID_USER_RE = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-.+$/i;
const USER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Down messages carry the acting user in "A"; others embed it in the client_id.
// "A" also holds non-user values (e.g. "1" on token messages) — only a full UUID counts.
function extractActorId(payload) {
    if (!payload) return null;
    if (typeof payload.A === 'string' && USER_ID_RE.test(payload.A)) return payload.A;
    const clientId = payload.client_id || payload.clientId;
    const match = typeof clientId === 'string' ? clientId.match(CLIENT_ID_USER_RE) : null;
    return match ? match[1] : null;
}

const httpMethods = {
    0: "GET", 1: "POST", 2: "PUT", 3: "DELETE", 4: "PATCH"
}

const CLIENT_MAPPING = {
    '0b': <><FontAwesomeIcon icon={faAmazon}/> Alexa</>,
    '06': 'Bond Local API',
    '01': <><FontAwesomeIcon icon={faAndroid} className={'fa-lg'}/> Android</>,
    '02': <><FontAwesomeIcon icon={faApple}/> iOS</>,
    '03': 'Backend',
    '0c': <><FontAwesomeIcon icon={faGoogle}/> Google</>,
    '0d': 'SmartThings',
    '0e': 'Android Widget',
    '0f': 'iOS Widget',
    '10': 'iOS Siri',
    '05': 'Bond',
    '21': 'iOS HomeKit',
    '0a': 'Bond Schedule',
    '55': <><LoadingIcon isLoading={false} className={"mqttBondClientIcon"}/> Palantir</>,
    '1c': <><LoadingIcon isLoading={false} className={"mqttBondClientIcon"}/> Mate</>,
};

const CLIENT_MAPPING_STYLE = {
    '01': 'success',
    '02': 'success',
    '55': 'secondary',
    '05': 'secondary',
};

// Custom style overrides (takes precedence over CLIENT_MAPPING_STYLE).
// Smart integrations use a dark fill + vivid brand border so they stand apart
// from the solid pills everywhere else in the feed.
const CLIENT_MAPPING_COLORS = {
    '1c': { backgroundColor: '#5ba4cf', color: '#ffffff' },                                                   // light blue for Mate
    '0c': { backgroundColor: '#06180e', color: '#a7f3c9', border: '2px solid #4ade80' },                      // Google green outline
    '0b': { backgroundColor: '#03202c', color: '#8ae9ff', border: '2px solid #1fc7ff', fontWeight: 800 },     // Alexa cyan outline, bold
    '3x': { backgroundColor: '#FF0F0F11', color: '#FFB7784E', border: '2px solid #FFB7784E' },                // Lite App orange outline
};


function MessageRow({ e, index, timezone }) {
    const [showJson, setShowJson] = useState(false);
    const topic = e.payload?.topic ?? e.payload?.event?.topic ?? '';
    const direction = e.payload?.direction ?? e.payload?.event?.direction ?? '';
    const isUp = topic.includes('/up/') || topic.includes('/gw-up/') || direction === 'gw-up';
    const date = new Date(e.timestamp);
    const timeString = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        fractionalSecondDigits: 3, timeZone: timezone, hour12: false,
    }).format(date);

    return (
        <div className="pt-2 d-flex flex-column" key={index}>
            <div className="d-flex align-items-center">
                <Badge
                    className={"me-1 d-flex align-items-center justify-content-center " + (isUp ? "upMessage" : "downMessage")}
                    pill={true}
                    style={{fontSize: '0.75rem', minHeight: '30px'}}
                >
                    <FontAwesomeIcon icon={isUp ? faArrowUp : faArrowDown} size="sm"/>
                </Badge>
                <Badge
                    bg={isUp ? "success" : "secondary"}
                    className={"me-1 d-flex align-items-center " + (isUp ? "upMessage" : "downMessage")}
                    style={{fontSize: '0.75rem', minHeight: '30px'}}
                >
                    {timeString}
                </Badge>
                <Badge
                    bg={showJson ? "secondary" : "dark"}
                    className="d-flex align-items-center justify-content-center json-toggle-btn"
                    style={{cursor: 'pointer', fontSize: '0.75rem', padding: '0.25rem 0.5rem', minHeight: '30px'}}
                    onClick={() => setShowJson(s => !s)}
                    title="Toggle JSON view"
                >
                    {'{}'}
                </Badge>
            </div>
            {showJson && (
                <ReactJsonView
                    src={e.payload}
                    className="mqttEventPayload"
                    theme="ocean"
                    borderStyle="none"
                    name={false}
                    displayObjectSize={false}
                    displayDataTypes={false}
                    collapsed={4}
                />
            )}
        </div>
    );
}

function MqttEvent({event, eventsByRequestId, bondsData = {}, homeMembers = [], dashboardUserId = null}) {
    // ALL React Hooks must be called FIRST, before any conditional returns
    const { timezone } = useTimezone();
    const [showMessages, setShowMessages] = useState(false);
    const [showRequestId, setShowRequestId] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [isTimeout, setTimeout] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const checkWaiting = useCallback(() => {
        // Safe access to event properties
        if (!event || !event.payload) return;
        const requestId = event.payload.i?.toLowerCase();
        if (!requestId || !eventsByRequestId[requestId]) return;

        let hasResponse = false;
        eventsByRequestId[requestId].forEach(e => {
            if (e.payload?.s ?? e.payload?.event?.s) hasResponse = true;
        });

        if (hasResponse) {
            setIsWaiting(false);
            setTimeout(false);
        } else {
            const eventTime = new Date(event.timestamp).getTime();
            const currentTime = Date.now();
            const ageInSeconds = (currentTime - eventTime) / 1000;

            if (ageInSeconds > 10) {
                setTimeout(true);
                setIsWaiting(false);
            } else {
                setIsWaiting(true);
                setTimeout(false);
            }
        }
    }, [event, eventsByRequestId]);

    useEffect(() => checkWaiting(), [event, checkWaiting]);

    useEffect(() => {
        let interval;
        if (isWaiting) {
            interval = setInterval(() => {
                checkWaiting();
                setRefreshKey(prev => prev + 1);
            }, 500); // Check every second
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isWaiting, checkWaiting]);

    // NOW we can validate - AFTER all hooks are called
    // Validate event structure - log and skip if malformed
    if (!event || !event.payload) {
        console.error('❌ MqttEvent: Malformed event received (missing event or payload):', event);
        return null;
    }


    function methodBadge() {
        const method = httpMethods[event.payload.m]

        let variant = "success";
        if (method === "DELETE") variant = "danger";
        else if (["POST", "PUT", "PATCH"].includes(method)) variant = "primary";

        // Check if request failed (timeout or status >= 400)
        let hasFailed = isTimeout;
        const requestId = event.payload.i?.toLowerCase();
        if (!hasFailed && requestId && eventsByRequestId[requestId]) {
            eventsByRequestId[requestId].forEach(e => {
                const s = e.payload?.s ?? e.payload?.event?.s;
                if (s && s >= 400) hasFailed = true;
            });
        }

        const style = {
            fontSize: '0.75rem',
            ...(hasFailed && { border: '2px solid #dc3545' })
        };

        return <Badge bg={variant} style={style}>{method}</Badge>
    }

    function statusBadge() {
        if (isWaiting)
            return <Badge bg={"warning"} pill={true} className={"me-1 text-black"} style={{fontSize: '0.75rem'}}>Waiting</Badge>;
        if (isTimeout)
            return <Badge bg={"danger"} pill={true} className={"me-1 text-black"} style={{fontSize: '0.75rem'}}>Timeout</Badge>;

        let response = null;
        const requestId = event.payload.i?.toLowerCase();
        if (requestId && eventsByRequestId[requestId])
            eventsByRequestId[requestId].forEach(e => {
                const s = e.payload?.s ?? e.payload?.event?.s;
                if (s) response = s;
            })
        if (response === null) return null;
        if (response < 300)
            return <Badge bg={"success"} pill={true} className={"me-1"} style={{fontSize: '0.75rem'}}>{response}</Badge>;
        else if (response < 400)
            return <Badge bg={"warning"} pill={true} className={"me-1"} style={{fontSize: '0.75rem'}}>{response}</Badge>;
        else if (response < 600)
            return <Badge bg={"danger"} pill={true} className={"me-1"} style={{fontSize: '0.75rem'}}>{response}</Badge>;
        else
            return <Badge bg={"secondary"} pill={true} className={"me-1"} style={{fontSize: '0.75rem'}}>{response}</Badge>;
    }

    function parseTopic(topic) {
        // Safety check for undefined topic
        if (!topic || typeof topic !== 'string') {
            return '';
        }

        const gwUpIdx = topic.indexOf('/gw-up/');
        if (gwUpIdx !== -1) return topic.substring(gwUpIdx + 6);
        const gwDownIdx = topic.indexOf('/gw-down/');
        if (gwDownIdx !== -1) return topic.substring(gwDownIdx + 8);
        const upIndex = topic.indexOf('/up/');
        if (upIndex !== -1) return topic.substring(upIndex + 3);
        const downIndex = topic.indexOf('/down/');
        return downIndex !== -1 ? topic.substring(downIndex + 5) : topic;
    }

    // When the actor is NOT the dashboard user (another member of the home), show
    // who acted — same pill as the members list. The displayed row is often the
    // response message (which has no "A"), so scan the related messages of the
    // same request id too, like methodBadge/statusBadge do.
    function getActorBadge() {
        let actorId = extractActorId(event.payload) || extractActorId(event.payload?.event);
        if (!actorId) {
            const requestId = event.payload.i?.toLowerCase();
            for (const e of (requestId && eventsByRequestId[requestId]) || []) {
                actorId = extractActorId(e.payload) || extractActorId(e.payload?.event);
                if (actorId) break;
            }
        }
        if (!actorId || actorId === dashboardUserId) return null;

        const member = Array.isArray(homeMembers) ? homeMembers.find(m => m.user_id === actorId) : null;
        const label = member?.email || member?.name || `${actorId.slice(0, 8)}…`;

        return (
            <Badge className="me-1 integrationBadge" style={{'--integration-badge-bg': '#8da9c4', '--integration-badge-color': '#1c2b3a', fontSize: '0.75rem'}} title={actorId}>
                <FontAwesomeIcon icon={faUser} className="me-1" size="xs"/>{label}
            </Badge>
        );
    }

    function getClientBadge() {
        // Check if payload.i exists
        if (!event.payload?.i) {
            return <></>;
        }

        let client = null;
        try {
            const requestIdPrefix = event.payload.i.substring(0, 2).toLowerCase();
            client = CLIENT_MAPPING[requestIdPrefix];
            const clientStyle = CLIENT_MAPPING_STYLE[requestIdPrefix] || "secondary";
            let clientColors = CLIENT_MAPPING_COLORS[requestIdPrefix];

            // Any request id starting with "3" (3x) is the Lite App
            if (!client && requestIdPrefix.startsWith('3')) {
                client = <><FontAwesomeIcon icon={faMobileScreenButton}/> Lite App</>;
                clientColors = CLIENT_MAPPING_COLORS['3x'];
            }

            if (client) {
                // Custom colors go through the integrationBadge CSS vars — a bare
                // inline background loses to the theme's `.badge.bg-*` !important
                // rules (Badge defaults to bg-primary when `bg` is undefined).
                return <Badge
                              bg={clientColors ? undefined : clientStyle}
                              className={"me-1 clientBadge" + (clientColors ? " integrationBadge" : "")}
                              pill={true}
                              style={{fontSize: '0.75rem', ...(clientColors && {
                                  '--integration-badge-bg': clientColors.backgroundColor,
                                  '--integration-badge-color': clientColors.color,
                                  border: clientColors.border,
                                  fontWeight: clientColors.fontWeight,
                              })}}
                              onClick={() => setShowRequestId(!showRequestId)}>{client} {showRequestId ? event.payload.i : ""}</Badge>
            } else {
                return <Badge bg={"secondary"} className={"me-1 clientBadge"}
                              pill={true}
                              style={{fontSize: '0.75rem'}}>({event.payload.i.substring(0, 2)})</Badge>
            }
        } catch {
            return <></>
        }
    }

    function getResourceBadges(topic) {
        const badges = [];

        // Check if topic is defined
        if (!topic) return badges;

        // Extract bond_id from event
        const bondId = event.bond_id;
        const bondData = bondsData[bondId];

        if (!bondData) return badges;

        // Parse topic to find resource IDs
        // Pattern examples: /devices/<device_id>/..., /groups/<group_id>/..., /scenes/<scene_id>/...

        // Check for device ID
        const deviceMatch = topic.match(/\/devices\/([a-f0-9]+)/i);

        if (deviceMatch && deviceMatch[1]) {
            const deviceId = deviceMatch[1];
            const paddedDeviceId = padDeviceId(deviceId);

            // Find device in bondData - match against both padded and unpadded IDs
            const devices = Array.isArray(bondData.devices) ? bondData.devices : Object.values(bondData.devices || {});
            const device = devices.find(d => {
                const dId = d.device_id || d.id;
                const paddedDId = padDeviceId(dId);
                return dId === deviceId || dId === paddedDeviceId || paddedDId === deviceId || paddedDId === paddedDeviceId;
            });

            if (device && device.name) {
                badges.push(
                    <Badge key={`device-${deviceId}`} bg="warning" text="dark" className="me-1" pill={true} style={{fontSize: '0.75rem'}}>
                        {device.name}
                    </Badge>
                );
            }
        }

        // Check for group ID
        const groupMatch = topic.match(/\/groups\/([a-f0-9]+)/i);
        if (groupMatch && groupMatch[1]) {
            const groupId = groupMatch[1];
            // Find group in bondData
            const groups = Array.isArray(bondData.groups) ? bondData.groups : Object.values(bondData.groups || {});
            const group = groups.find(g => g.group_id === groupId || g.id === groupId);
            if (group && group.name) {
                badges.push(
                    <Badge key={`group-${groupId}`} bg="warning" className="me-1" pill={true} style={{fontSize: '0.75rem'}}>
                        {group.name}
                    </Badge>
                );
            }
        }

        // Check for scene ID
        const sceneMatch = topic.match(/\/scenes\/([a-f0-9]+)/i);
        if (sceneMatch && sceneMatch[1]) {
            const sceneId = sceneMatch[1];
            // Find scene in bondData
            const scenes = Array.isArray(bondData.scenes) ? bondData.scenes : Object.values(bondData.scenes || {});
            const scene = scenes.find(s => s.scene_id === sceneId || s.id === sceneId);
            if (scene && scene.name) {
                badges.push(
                    <Badge key={`scene-${sceneId}`} bg="primary" className="me-1" pill={true} style={{fontSize: '0.75rem'}}>
                        {scene.name}
                    </Badge>
                );
            }
        }

        // Check for sidekick ID
        const sidekickMatch = topic.match(/\/sidekicks\/([a-zA-Z0-9_-]+)/i);
        if (sidekickMatch && sidekickMatch[1]) {
            const sidekickId = sidekickMatch[1];
            // Find sidekick in bondData
            const sidekicks = Array.isArray(bondData.sidekicks) ? bondData.sidekicks : Object.values(bondData.sidekicks || {});
            const sidekick = sidekicks.find(s => (s.id === sidekickId || s.sidekick_id === sidekickId));
            if (sidekick && sidekick.name) {
                badges.push(
                    <Badge key={`sidekick-${sidekickId}`} bg="success" className="me-1" pill={true} style={{fontSize: '0.75rem'}}>
                        {sidekick.name}
                    </Badge>
                );
            }
        }

        // Check for schedule ID (sked)
        const skedMatch = topic.match(/\/skeds\/([a-f0-9]+)/i);
        if (skedMatch && skedMatch[1]) {
            const skedId = skedMatch[1];
            // Schedules can be in devices, groups, or scenes
            let scheduleName = null;

            // Check devices
            const devices = Array.isArray(bondData.devices) ? bondData.devices : Object.values(bondData.devices || {});
            devices.forEach(device => {
                if (device.schedules) {
                    const schedules = Array.isArray(device.schedules) ? device.schedules : Object.values(device.schedules);
                    const schedule = schedules.find(s => s.sked_id === skedId || s.id === skedId);
                    if (schedule && schedule.name) {
                        scheduleName = schedule.name;
                    }
                }
            });

            // Check groups if not found
            if (!scheduleName) {
                const groups = Array.isArray(bondData.groups) ? bondData.groups : Object.values(bondData.groups || {});
                groups.forEach(group => {
                    if (group.schedules) {
                        const schedules = Array.isArray(group.schedules) ? group.schedules : Object.values(group.schedules);
                        const schedule = schedules.find(s => s.sked_id === skedId || s.id === skedId);
                        if (schedule && schedule.name) {
                            scheduleName = schedule.name;
                        }
                    }
                });
            }

            // Check scenes if not found
            if (!scheduleName) {
                const scenes = Array.isArray(bondData.scenes) ? bondData.scenes : Object.values(bondData.scenes || {});
                scenes.forEach(scene => {
                    if (scene.schedules) {
                        const schedules = Array.isArray(scene.schedules) ? scene.schedules : Object.values(scene.schedules);
                        const schedule = schedules.find(s => s.sked_id === skedId || s.id === skedId);
                        if (schedule && schedule.name) {
                            scheduleName = schedule.name;
                        }
                    }
                });
            }

            if (scheduleName) {
                badges.push(
                    <Badge key={`sked-${skedId}`} bg="secondary" className="me-1" pill={true} style={{fontSize: '0.75rem'}}>
                        {scheduleName}
                    </Badge>
                );
            }
        }

        return badges;
    }

    function getMessageRequests() {
        const requestId = event.payload.i?.toLowerCase();
        if (!requestId) return null;

        if (eventsByRequestId[requestId])
            eventsByRequestId[requestId] = eventsByRequestId[requestId].sort((a, b) => b.timestamp - a.timestamp);

        return eventsByRequestId[requestId]?.map((e, index) => (
            <MessageRow key={index} e={e} index={index} timezone={timezone} />
        ))
    }

    function rowContent() {
        if (event.action === "message") {
            // No request ID — render as a simple standalone message row
            if (!event.payload.i) {
                return <MessageRow e={event} index={0} timezone={timezone} />;
            }
            return <>
                <div style={{display: 'flex', gap: '0.5rem', alignItems: 'flex-start'}}>
                    <div style={{flex: '0 0 50%', display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'flex-start'}}>
                        {getActorBadge()}{getClientBadge()}{methodBadge()} {statusBadge()}
                        {getResourceBadges(event.payload.topic)}
                    </div>
                    <div style={{flex: '0 0 50%'}}>
                        <input type="text" value={parseTopic(event.payload.topic)} className={"mqttEventTopic w-100"}
                               readOnly/>
                    </div>
                </div>
                {getMessageRequests()}
            </>
        }
        else if (event.action === "connected")
            return <><span>
                <Badge className={"me-1 bondBadge"} pill={true} style={{'--bond-badge-bg': getBondColor(event.bond_id), fontSize: '0.75rem'}}>{event.bond_id}</Badge>
                <Badge bg={"success"} className={"me-2"} pill={true} style={{fontSize: '0.75rem'}}>Connected</Badge>
            </span>
                <span className={"float-end pe-2"}>{event.payload.ip}</span>
            </>
        else if (event.action === "disconnected")
            return <><span>
                <Badge className={"me-1 bondBadge"} pill={true} style={{'--bond-badge-bg': getBondColor(event.bond_id), fontSize: '0.75rem'}}>{event.bond_id}</Badge>
                <Badge bg={"danger"} className={"me-2"} pill={true} style={{fontSize: '0.75rem'}}>Disconnected</Badge>
            </span>
                <span className={"float-end pe-2"}>{event.payload.reason}</span>
            </>
        else if (event.payload?.type === 'BOND' || event.action === 'DEVICE_UPDATE') {
            const attrs = event.payload?.attributes || {};
            const { device_name, device_type, device_location, field, value_changed } = attrs;
            return <div style={{maxWidth: '100%', overflow: 'hidden'}}>
                <div className="d-flex align-items-center flex-wrap gap-1">
                    <Badge style={{background: '#a371f7', fontSize: '0.75rem'}}>
                        {event.action || event.payload?.action || 'BOND'}
                    </Badge>
                    {device_type && <Badge bg="secondary" style={{fontSize: '0.75rem'}}>{device_type}</Badge>}
                    {device_name && <Badge bg="warning" text="dark" style={{fontSize: '0.75rem'}}>{device_name}</Badge>}
                    {device_location && <Badge bg="light" text="dark" style={{fontSize: '0.75rem'}}>📍 {device_location}</Badge>}
                    {field && <>
                        <span style={{color: '#6e7681', fontSize: '0.75rem'}}>›</span>
                        <Badge bg="secondary" style={{fontSize: '0.75rem'}}>{field}</Badge>
                    </>}
                    {value_changed === false && <Badge bg="secondary" pill style={{fontSize: '0.7rem'}}>unchanged</Badge>}
                    {value_changed === true && <Badge bg="success" pill style={{fontSize: '0.7rem'}}>changed</Badge>}
                    <Badge
                        bg={showMessages ? 'secondary' : 'dark'}
                        style={{cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none'}}
                        onClick={() => setShowMessages(s => !s)}
                        title="Toggle raw JSON"
                    >{'{}'}</Badge>
                </div>
                {showMessages && (
                    <div style={{marginTop: '6px', maxWidth: '100%', overflowX: 'auto'}}>
                        <ReactJsonView
                            src={event.payload}
                            className="mqttEventPayload"
                            theme="ocean"
                            borderStyle="none"
                            name={false}
                            displayObjectSize={false}
                            displayDataTypes={false}
                            collapsed={2}
                        />
                    </div>
                )}
            </div>
        }
        else
            return <div style={{maxWidth: '100%', overflow: 'hidden'}}>
                <Badge
                    bg={showMessages ? 'secondary' : 'dark'}
                    style={{cursor: 'pointer', fontSize: '0.75rem', userSelect: 'none'}}
                    onClick={() => setShowMessages(s => !s)}
                >▼ raw</Badge>
                {showMessages && (
                    <div style={{marginTop: '6px', maxWidth: '100%', overflowX: 'auto'}}>
                        <ReactJsonView
                            src={event.payload}
                            className="mqttEventPayload"
                            theme="ocean"
                            borderStyle="none"
                            name={false}
                            displayObjectSize={false}
                            displayDataTypes={false}
                            collapsed={2}
                        />
                    </div>
                )}
            </div>
    }

    // Wrap render in try-catch to prevent component crashes
    try {
        return <div key={refreshKey} style={{maxWidth: '100%', overflow: 'hidden'}}>
            {rowContent()}
        </div>;
    } catch (error) {
        console.error('❌ MqttEvent: Error rendering event:', {
            error: error.message,
            stack: error.stack,
            event: event,
            payload: event.payload
        });
        // Return a placeholder instead of crashing
        return (
            <div style={{color: 'red', padding: '5px', fontSize: '0.8rem'}}>
                ⚠️ Error rendering event - check console for details
            </div>
        );
    }
}

export default MqttEvent;
